import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { TranslationNamespace } from 'i18n';
import { PaymentRedirectViewProps } from 'types';

const COUNTDOWN_SECONDS = 10;
const RING_SIZE = 56;
const RING_STROKE = 4;
const RING_RADIUS = (RING_SIZE - RING_STROKE) / 2;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

// The "go to payment" button lives in the SqLayout footer. The countdown is visual only: re-assigning the link
// would abort a slow first navigation and can burn a one-time provider checkout session.
export const SqPaymentRedirect: React.FC<PaymentRedirectViewProps> = ({
  paymentLink,
}) => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'components.ecom_redirect',
  });
  const redirectAttemptedRef = useRef(false);
  const [remaining, setRemaining] = useState(COUNTDOWN_SECONDS);

  useEffect(() => {
    if (redirectAttemptedRef.current) {
      return;
    }
    redirectAttemptedRef.current = true;
    window.location.assign(paymentLink);
  }, [paymentLink]);

  useEffect(() => {
    if (remaining <= 0) {
      return;
    }
    const timeoutId = setTimeout(() => setRemaining(remaining - 1), 1000);
    return () => clearTimeout(timeoutId);
  }, [remaining]);

  return (
    <div
      data-testid="sq-payment-redirect"
      className="tw-flex tw-flex-col tw-flex-1 tw-items-center tw-justify-center tw-gap-3 tw-text-center"
    >
      <div
        className="tw-relative tw-flex tw-items-center tw-justify-center"
        style={{
          width: RING_SIZE,
          height: RING_SIZE,
          color: 'var(--bg-color-primary-button)',
        }}
      >
        <svg
          className="tw-absolute tw-inset-0 -tw-rotate-90"
          width={RING_SIZE}
          height={RING_SIZE}
          viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}
          fill="none"
        >
          <circle
            className="tw-opacity-20"
            cx={RING_SIZE / 2}
            cy={RING_SIZE / 2}
            r={RING_RADIUS}
            stroke="currentColor"
            strokeWidth={RING_STROKE}
          />
          <circle
            cx={RING_SIZE / 2}
            cy={RING_SIZE / 2}
            r={RING_RADIUS}
            stroke="currentColor"
            strokeWidth={RING_STROKE}
            strokeLinecap="round"
            strokeDasharray={RING_CIRCUMFERENCE}
            strokeDashoffset={
              RING_CIRCUMFERENCE * (1 - remaining / COUNTDOWN_SECONDS)
            }
            style={{ transition: 'stroke-dashoffset 1s linear' }}
          />
        </svg>
        <span className="tw-text-xl tw-font-semibold">{remaining}</span>
      </div>
      <span className="tw-text-xl tw-font-semibold tw-text-black tw-tracking-[-0.03em] tw-leading-[1.2]">
        {t('title')}
      </span>
      <span className="tw-text-sm tw-text-[#777777]">{t('hint')}</span>
    </div>
  );
};
